import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { AssessmentTable } from "@/components/assessment-table";
import { DeleteAssessmentDialog } from "@/components/delete-assessment-dialog";
import { Eyebrow } from "@/components/eyebrow";
import { PageShell } from "@/components/page-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { type AssessmentSummary, useMyAssessments } from "@/lib/api";

export function HistoryPage() {
  const query = useMyAssessments();
  const rows = query.data ?? [];
  const [deleteTarget, setDeleteTarget] = useState<AssessmentSummary | null>(null);

  return (
    <PageShell wide>
      <Eyebrow>Your history</Eyebrow>
      <h1 className="text-primary mt-3 text-4xl font-normal tracking-tight sm:text-5xl">
        Past assessments.
      </h1>
      <p className="text-muted-foreground mt-4 mb-7 text-lg">
        Every assessment you’ve completed. Open one to revisit its report.
      </p>

      {query.isLoading && (
        <div className="flex justify-center py-24">
          <Loader2 className="text-muted h-6 w-6 animate-spin" />
        </div>
      )}
      {query.error != null && (
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground text-sm">
              Couldn’t load your history. Please try again.
            </p>
            <Button variant="outline" className="mt-4" onClick={() => void query.refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}
      {query.data &&
        (rows.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <p className="text-muted text-sm">
                No assessments yet. Complete an assessment and it will appear here.
              </p>
              <Button asChild className="mt-4">
                <Link to="/assess">Start an assessment</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <AssessmentTable
            rows={rows}
            linkLabel="View report →"
            linkTo={(id) => `/report/${id}`}
            onDelete={setDeleteTarget}
          />
        ))}

      <DeleteAssessmentDialog
        assessmentId={deleteTarget?.assessment_id ?? null}
        companyName={deleteTarget?.company_name ?? null}
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      />
    </PageShell>
  );
}
